import { Mask } from '@shared/models/live';

export type MaskHandle = 'ne' | 'nw' | 'n' | 'sw' | 'w' | 's' | 'se' | 'e';
export type MaskRect = Pick<Mask, 'X' | 'Y' | 'Width' | 'Height'>;
export type MaskCursor = `${MaskHandle}-resize`;

export interface MaskPoint {
  x: number;
  y: number;
}

export interface MaskBounds {
  width: number;
  height: number;
}

// Hit-test priority when handles overlap on a small mask; keep this order.
export const MASK_HANDLES: readonly MaskHandle[] = ['ne', 'nw', 'n', 'sw', 'w', 's', 'se', 'e'];

export const MASK_HANDLE_ANCHORS: Readonly<Record<MaskHandle, readonly [fx: number, fy: number]>> = {
  ne: [1, 0],
  nw: [0, 0],
  n: [0.5, 0],
  sw: [0, 1],
  w: [0, 0.5],
  s: [0.5, 1],
  se: [1, 1],
  e: [1, 0.5],
};

export function hitHandle(mask: MaskRect, point: MaskPoint, tol: MaskPoint): MaskHandle | null {
  return (
    MASK_HANDLES.find((handle) => {
      const [fx, fy] = MASK_HANDLE_ANCHORS[handle];
      const ax = mask.X + mask.Width * fx;
      const ay = mask.Y + mask.Height * fy;
      return point.x >= ax - tol.x && point.x <= ax + tol.x && point.y >= ay - tol.y && point.y <= ay + tol.y;
    }) ?? null
  );
}

export function resizeMask(mask: MaskRect, handle: MaskHandle, x: number, y: number): MaskRect {
  const [fx, fy] = MASK_HANDLE_ANCHORS[handle];
  const allowed =
    (fx !== 1 || x - mask.X > 0) && (fx !== 0 || mask.Width + mask.X - x > 0) && (fy !== 1 || y - mask.Y > 0) && (fy !== 0 || mask.Height + mask.Y - y > 0);
  const next = { X: mask.X, Y: mask.Y, Width: mask.Width, Height: mask.Height };
  if (!allowed) return next;

  if (fx === 1) next.Width = x - mask.X;
  if (fx === 0) {
    next.Width = mask.Width + (mask.X - x);
    next.X = x;
  }
  if (fy === 1) next.Height = y - mask.Y;
  if (fy === 0) {
    next.Height = mask.Height + (mask.Y - y);
    next.Y = y;
  }
  return next;
}

function clampCentred(position: number, centre: number, length: number, limit: number): number {
  if (0 <= centre - length / 2 && limit >= centre + length / 2) return centre - Math.round(length / 2);
  if (0 > centre - length / 2) return 0;
  if (limit < centre + length / 2) return limit - length;
  return position;
}

export function dragMask(mask: MaskRect, x: number, y: number, bounds: MaskBounds): Pick<MaskRect, 'X' | 'Y'> {
  return {
    X: clampCentred(mask.X, x, mask.Width, bounds.width),
    Y: clampCentred(mask.Y, y, mask.Height, bounds.height),
  };
}

export function cursorFor(handle: MaskHandle): MaskCursor {
  return `${handle}-resize`;
}
